/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

// Component imports
import { Button, Card, Checkbox, Col, Collapse, Divider, Empty, Form, Input, InputNumber, Row, Select, Space, Spin, Switch, Typography } from 'antd';
import { SearchOutlined, WarningOutlined } from '@ant-design/icons';
import DeleteConfirmationModal from '../Modals/DeleteConfirmationModal';
import EntryPointSelect from './Fields/EntryPointSelect';
import MultiDropdown from './Fields/MultiDropdown';
import FormCard from './FormCard';
import BotDisabledModalWarning from '../Modals/BotDisabledModalWarning';
import Prompt from '../UI/Prompt';
import AvatarFormFieldSelection from '../UI/AvatarFormFieldSelection';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';

// Misc imports
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import is from '../../utils/is';
import { LANGUAGE_OPTIONS, PERMISSIONS_GROUP_ADMIN, ACTIVITY_TYPES, POWERUPS, DEFAULT_DELETE_CONFIRMATION_KEYWORD } from '../../constants/main';
import { IS_USER_FEEDBACK_ENABLED } from '../../constants/env';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useDeleteBotMutation, useUpdateBotMutation } from '../../store/bots/botsApi';
import { useFetchMediaFilesQuery } from '../../store/media/mediaApi';
import { updateRecentBots } from '../../store/bots/botsActions';
import { NAV_BOTS } from '../../constants/navigation';
import { useDispatch, useSelector } from 'react-redux';
import useTheme from '../../utils/hooks/useTheme';

// Constant declarations
const timezoneOptions = moment.tz.names();

const BotSettingsForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { formProps } = useTheme();
  const { tenantId, orgId } = useParams();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const canEdit = userPermissions.some(permission => PERMISSIONS_GROUP_ADMIN.includes(permission));
  const { recentBots } = useSelector((state) => state.bots);

  const { data: bot, isFetching: isFetchingBot, isError: fetchBotError } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const [updateBot, { isLoading: isUpdatingBot }] = useUpdateBotMutation({ fixedCacheKey: `updateTenantSettings` });
  const [deleteBot, { isLoading: isDeletingBot }] = useDeleteBotMutation();

  const { isFetching: isFetchingMediaFiles } = useFetchMediaFilesQuery({ orgId, urlParams: {
    view: 'grid',
    page: 1,
    pageSize: 16,
    sortBy: 'createdAt',
    order: 'DESC'
  } }, { skip: !orgId || !tenantId });

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isBotDisabledModalOpen, setIsBotDisabledModalOpen] = useState(false);
  const [isFieldsTouched, setIsFieldsTouched] = useState(false);
  const [, updateState] = useState();

  const powerupsToDisplay = (bot.powerups || []).filter(powerup =>
    powerup === POWERUPS.AUTOMATED_ANSWERS.key
    || powerup === POWERUPS.CHAT_SESSIONS.key
    || powerup === POWERUPS.SURVEYS.key
    || powerup === POWERUPS.FLOWS.key
    || powerup === POWERUPS.BACKUPS.key
    || powerup === POWERUPS.BROADCASTS.key
  );

  const openDeleteModal = () => {
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const handleNLPLanguageMapRemoval = (updatedLanguages, currentNlpMap) => {
    const incomingLanguages = updatedLanguages?.length > 0 ? updatedLanguages : [];
    const botLanguages = [bot?.settings?.languages?.primary, ...incomingLanguages];
    let newNlpMap = {};
    const currentMappedLanguages = Object.keys(currentNlpMap);

    currentMappedLanguages?.forEach(language => {
      if(botLanguages?.includes(language)) {
        newNlpMap[language] = currentNlpMap[language]?.id;
      }
    });
    return newNlpMap;
  };

  const onSubmit = async (values) => {
    const additionalLanguages = values?.settings?.languages?.additional;
    const valuesToSubmit = {
      ...values,
      ...(Object.keys(bot?.nlpMap).length > 0 && { nlpMapRef: handleNLPLanguageMapRemoval(additionalLanguages, bot.nlpMap) }),
      settings: {
        ...values?.settings,
        feedback: {
          ...values?.settings?.feedback,
          thumbsUpNodeId: values?.settings?.feedback?.thumbsUpNodeId ?? null,
          thumbsDownNodeId: values?.settings?.feedback?.thumbsDownNodeId ?? null
        }
      }
    };

    const isSaveSuccessful = await updateBot({ orgId, tenantId, data: valuesToSubmit }).unwrap();
    if (isSaveSuccessful) {
      setIsFieldsTouched(false);
    }

    return;
  };

  const onDelete = async () => {
    const isDeleted = await deleteBot({ orgId, tenantId }).unwrap();
    if (isDeleted) {
      dispatch(updateRecentBots(recentBots.filter((bot) => bot.id !== tenantId)));
      navigate(replaceUrlParams(NAV_BOTS, { orgId }));
    }
  };

  const renderDeleteBotModal = () => {
    return (
      <Fragment>
        <Row gutter={formProps.rowGutter} className="mt-3">
          <Col span={24} className ="d-flex justify-content-center">
            {
              !is.emptyArray(powerupsToDisplay) ? (
                <Typography.Paragraph>
                  {t('You will permanently lose your')}:
                </Typography.Paragraph>
              ) : (
                <Typography.Paragraph>
                  {t('Agent settings will be permanently lost')}.
                </Typography.Paragraph>
              )
            }
          </Col>
        </Row>

        <Row gutter={formProps.rowGutter}>
          <Col span={24} className ="d-flex justify-content-center">
            <Typography.Paragraph>
              <ul>
                {
                  powerupsToDisplay.map(powerup => (
                    <li>
                      {t(powerup, { context: 'powerup' })}
                    </li>
                  ))
                }
              </ul>
            </Typography.Paragraph>
          </Col>
        </Row>
      </Fragment>
    );
  };

  const renderContent = () => {
    if (!bot && isFetchingBot) {
      return (
        <Card>
          <Row justify="center">
            <Col span={24}>
              <Spin className="d-block" size="large" spinning />
            </Col>
          </Row>
        </Card>
      );
    }

    if (fetchBotError) {
      return (
        <Empty
          description={
            <Row gutter={16} className="mt-3">
              <Col md={{ span: 12, offset: 6 }}>
                {t('There was an error fetching your Agent, please refresh your page. If the problem persists, please contact an administrator.')}
              </Col>
            </Row>
          }
          className="mt-3 mb-6"
        />
      );
    }

    if ((!bot || !bot?.settings) && !isFetchingBot) {
      return (
        <Empty
          description={
            <Row gutter={16} className="mt-3">
              <Col md={{ span: 12, offset: 6 }}>
                {t('There are no Agent Settings available. Please contact an administrator.')}
              </Col>
            </Row>
          }
          className="mt-3 mb-6"
        />
      );
    }

    if (bot) {
      return (
        <FormCard
          name="botSettingsForm"
          layout="vertical"
          labelCol={{ xs: 24, md: 10, xl: 8, xxl: 6 }}
          wrapperCol={{ xs: 24, md: 18, xl: 14, xxl: 12 }}
          onFinish={(values) => {
            if (values?.disabled !== bot?.disabled) {
              setIsBotDisabledModalOpen(true);
            } else {
              onSubmit(values);
            }
          }}
          form={form}
          initialValues={bot}
          onFieldsChange={(_, allFields) => {
            setIsFieldsTouched(true);
            updateState(allFields);
          }}
          footerActions={[
            canEdit && (
              <Button
                key="submit"
                type="primary"
                size="large"
                disabled={isFetchingBot || isUpdatingBot}
                onClick={() => form.submit()}
              >
                {t('Save Changes')}
              </Button>
            )
          ]}
        >
          <Row gutter={formProps.rowGutter}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text className="fs-base">
                {t('Agent Identity')}
              </Typography.Text>
              <Typography.Paragraph className="fs-sm" type="secondary">
                {t('Configure the identity of your agent')}
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              <Form.Item
                label={t('Name')}
                name={'displayName'}
                rules={[{ required: true, message: t('Please input a name for your agent.'), type: 'string' }]}
              >
                <Input
                  size="large"
                  disabled={!canEdit}
                  maxLength={240}
                  placeholder={t('Set agent name.')}
                />
              </Form.Item>

              <Form.Item
                label={t('Description')}
                name={'description'}
                rules={[{ required: false, message: t('Please input a description for your agent.'), type: 'string' }]}
              >
                <Input.TextArea
                  size="large"
                  disabled={!canEdit}
                  maxLength={400}
                  placeholder={t('Set agent short description.')}
                />
              </Form.Item>

              <Form.Item name="image" >
                <AvatarFormFieldSelection
                  fields={
                    {
                      inputField: null,
                      uploadField: {
                        label: t('Select Image to Upload'),
                        validationMessage: t('Please select or upload an image')
                      }
                    }
                  }
                  loading={isFetchingMediaFiles || isUpdatingBot}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider className="mt-2" />

          <Row gutter={formProps.rowGutter}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text className="fs-base">
                {t('Agent Settings')}
              </Typography.Text>
              <Typography.Paragraph className="fs-sm" type="secondary">
                {t('Configure the chat settings of your agent')}
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              <Form.Item
                label={t('Timezone')}
                name={'timezone'}
              >
                <Select
                  size="large"
                  className="w-100"
                  disabled={!canEdit}
                  placeholder={t('Select a timezone')}
                  options={timezoneOptions.map((timezone) => ({ label: timezone, value: timezone }))}
                  showSearch
                  getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
                />
              </Form.Item>

              <Form.Item
                label={t('Session Expiration') + ' (sec)'}
                name={['settings', 'session', 'expireInactiveSessionAfter']}
              >
                <InputNumber
                  size="large"
                  disabled={!canEdit}
                  placeholder={t('Please input a session expiration time in seconds.')}
                  step={1}
                  className="w-100"
                />
              </Form.Item>

              {
                IS_USER_FEEDBACK_ENABLED && (
                  <Fragment>
                    <Form.Item
                      name={['settings', 'feedback', 'enableThumbsFeedback']}
                      valuePropName="checked"
                      shouldUpdate
                    >
                      <Checkbox disabled={!canEdit}>{t('Enable Thumb Rating')}</Checkbox>
                    </Form.Item>

                    <Form.Item
                      shouldUpdate={(prevValues, currentValues) => prevValues?.settings?.feedback?.enableThumbsFeedback !== currentValues?.settings?.feedback?.enableThumbsFeedback}
                      className="mb-0"
                    >
                      {() => {
                        return (
                          form.getFieldValue(['settings', 'feedback', 'enableThumbsFeedback']) ? (
                            <Card
                              size="small"
                              className="bg-layout-gray shadow-none mb-5"
                            >
                              <Form.Item
                                label={t('Flow triggered with Thumbs Up')}
                                name={['settings', 'feedback', 'thumbsUpNodeId']}
                              >
                                <EntryPointSelect
                                  allowClear={true}
                                  activityTypesToDisplay={Object.values(ACTIVITY_TYPES)}
                                  disabled={!canEdit}
                                />
                              </Form.Item>
                              <Form.Item
                                label={t('Flow triggered with Thumbs Down')}
                                name={['settings', 'feedback', 'thumbsDownNodeId']}
                              >
                                <EntryPointSelect
                                  allowClear={true}
                                  activityTypesToDisplay={Object.values(ACTIVITY_TYPES)}
                                  disabled={!canEdit}
                                />
                              </Form.Item>
                            </Card>
                          ) : null
                        );
                      }}
                    </Form.Item>
                  </Fragment>
                )
              }
            </Col>
          </Row>

          <Divider className="mt-2" />

          <Row gutter={formProps.rowGutter}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text className="fs-base">
                {t('Agent Languages')}
              </Typography.Text>
              <Typography.Paragraph className="fs-sm" type="secondary">
                {t('Configure the languages of your Agent')}
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              <Form.Item
                label={t('Primary Language')}
              >
                <Space size="small">
                  {LANGUAGE_OPTIONS.find(lang => lang.key === bot.settings.languages.primary)?.icon}
                  {LANGUAGE_OPTIONS.find(lang => lang.key === bot.settings.languages.primary)?.label}
                </Space>
              </Form.Item>

              <Form.Item
                label={t('Set Additional Languages')}
                name={['settings', 'languages', 'additional']}
              >
                <MultiDropdown
                  size="large"
                  placeholder={<Space size={4}><SearchOutlined className="text-secondary" /> {t('Search available languages')}</Space>}
                  allowClear
                  disabled={!canEdit}
                  autoClearSearchValue={false}
                  options={LANGUAGE_OPTIONS
                    .filter(language => language.key !== bot.settings.languages.primary)
                    .map(language => ({
                      label: <Space size={4}>{LANGUAGE_OPTIONS.find(lang => lang.key === language.key)?.icon} {language.label}</Space>,
                      value: language.key,
                      searchValue: language.label
                    }))
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          {
            canEdit && (
              <Fragment>
                <Collapse defaultActiveKey={['1']} ghost>
                  <Collapse.Panel
                    showArrow={false}
                    header={
                      <Divider className="fs-base ant-divider-danger" orientation="left">
                        <Typography.Text strong>
                          <span className="title">
                            {t('DANGER ZONE')}
                          </span>
                          <span className="ml-3 icon">
                            <ArrowDownIcon className="fs-base" />
                          </span>
                        </Typography.Text>
                      </Divider>
                    }
                    key="1"
                  >
                    <Row gutter={formProps.rowGutter} className="mt-5">
                      <Col span={formProps.mainColSpan}>
                        <Typography.Text className="fs-base">
                          {t('Agent availability')}
                        </Typography.Text>
                        <Typography.Paragraph className="fs-sm" type="secondary">
                          {t('Affect the current availability of the Agent.')}
                        </Typography.Paragraph>
                      </Col>

                      <Col span={formProps.secondaryColSpan}>
                        <Form.Item
                          name="disabled"
                          label={t('Enable Agent')}
                        >
                          <Switch
                            disabled={!canEdit}
                            checked={!form.getFieldValue('disabled')}
                            onChange={(checked) => {
                              form.setFieldValue('disabled', !checked);
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={formProps.rowGutter}>
                      <Col span={24}>
                        <Typography.Text className="fs-base text-error">
                          <WarningOutlined className="mr-1 fs-lg" />  {t('In this section you can perform non-reversible actions that may affect the behavior of the agent.')}
                        </Typography.Text>
                      </Col>
                    </Row>

                    <Row gutter={formProps.rowGutter} className="mt-5">
                      <Col span={formProps.mainColSpan}>
                        <Typography.Text className="fs-base">
                          {t('Agent Deletion')}
                        </Typography.Text>
                        <Typography.Paragraph className="fs-sm" type="secondary">
                          {t('Delete the current Agent, including all the settings and backups.')}
                        </Typography.Paragraph>
                      </Col>

                      <Col span={formProps.secondaryColSpan}>
                        <Form.Item>
                          <Button
                            disabled={isFetchingBot}
                            hidden={!canEdit}
                            className="float-left"
                            type="primary"
                            size="large"
                            danger
                            onClick={openDeleteModal}
                          >
                            {`${t('Delete this agent')}`}
                          </Button>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Collapse.Panel>
                </Collapse>

                <DeleteConfirmationModal
                  title={t('Delete Agent Forever?')}
                  content={renderDeleteBotModal()}
                  onDelete={onDelete}
                  isDeleting={isDeletingBot}
                  isModalOpen={isDeleteModalOpen}
                  closeModal={closeDeleteModal}
                  showDeleteKeyword
                  deleteKeyword={DEFAULT_DELETE_CONFIRMATION_KEYWORD.BOT}
                />
              </Fragment>
            )
          }

          <Prompt
            when={isFieldsTouched}
            onConfirm={() => form.submit()}
            okText={t('Save Changes')}
            cancelText={t('Quit without saving')}
            title={t('Save Changes?')}
            message={t('Your unsaved changes will be lost. Save changes before closing?')}
          />

          <BotDisabledModalWarning
            isModalOpen={isBotDisabledModalOpen}
            closeModal={() => setIsBotDisabledModalOpen(false)}
            handleSave={() => {
              const values = form.getFieldsValue(true, (meta) => {
                return meta.touched;
              });
              onSubmit(values);
              setIsBotDisabledModalOpen(false);
            }}
          />
        </FormCard>
      );
    }
  };

  return renderContent();
};

export default BotSettingsForm;
